import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronRight } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { t } from '../translations';

// Nouveau logo Exevori avec fond transparent
const LOGO = "https://customer-assets.emergentagent.com/job_connected-work/artifacts/kalrhzgp_remove.photos-removed-background%20Rxevori.png";

export default function Navbar() {
  const { lang, toggleLang } = useLang();
  const tr = t[lang];
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  const navLinks = [
    { label: tr.nav.home, to: '/' },
    { label: tr.nav.services, to: '/services' },
    { label: tr.nav.portfolio, to: '/realisations' },
    { label: tr.nav.process, to: '/processus' },
    { label: tr.nav.about, to: '/a-propos' },
    { label: tr.nav.faq, to: '/faq' },
    { label: tr.nav.contact, to: '/contact' },
  ];

  const isActive = (to) => location.pathname === to;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass-header shadow-lg shadow-black/20' : 'bg-transparent'
      }`}
    >
      <div className="section-container">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo - côté gauche */}
          <Link to="/" data-testid="navbar-logo" className="flex items-center flex-shrink-0 w-[120px]">
            <img
              src={LOGO}
              alt="Exevori"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav - centré */}
          <div className="hidden lg:flex items-center justify-center gap-1 flex-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                className={`px-3 py-2 rounded-full text-sm font-medium font-inter transition-all duration-200 whitespace-nowrap ${
                  isActive(link.to)
                    ? 'text-exevori-primary bg-exevori-primary/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: Lang + CTA */}
          <div className="flex items-center justify-end gap-3 w-[120px] lg:w-auto">
            {/* Language Switcher */}
            <button
              data-testid="lang-switcher"
              onClick={toggleLang}
              className="hidden sm:flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-sm font-medium text-gray-300 hover:text-white hover:border-white/30 transition-all duration-200 font-inter"
            >
              <span className={lang === 'fr' ? 'text-exevori-primary' : 'text-gray-400'}>FR</span>
              <span className="text-gray-600">|</span>
              <span className={lang === 'en' ? 'text-exevori-primary' : 'text-gray-400'}>EN</span>
            </button>

            {/* CTA Button */}
            <Link
              to="/devis"
              data-testid="navbar-cta-quote"
              className="btn-primary text-sm hidden md:inline-flex"
            >
              {tr.nav.quote}
              <ChevronRight size={16} />
            </Link>

            {/* Mobile menu toggle */}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/5 transition-colors"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden glass-header border-t border-white/5"
        >
          <div className="section-container py-4 flex flex-col gap-1">
            {navLinks.map(link => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`mobile-nav-${link.to.replace('/', '') || 'home'}`}
                className={`px-4 py-3 rounded-xl text-sm font-medium font-inter transition-all ${
                  isActive(link.to)
                    ? 'text-exevori-primary bg-exevori-primary/10'
                    : 'text-gray-300 hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
              <button
                onClick={toggleLang}
                className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-sm text-gray-300"
              >
                <span className={lang === 'fr' ? 'text-exevori-primary' : ''}>FR</span>
                <span className="text-gray-600">|</span>
                <span className={lang === 'en' ? 'text-exevori-primary' : ''}>EN</span>
              </button>
              <Link to="/devis" className="btn-primary text-sm flex-1 justify-center">
                {tr.nav.quote}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
